import { Injectable } from "@angular/core";

export interface ShellSearchHit {
  id: string;
  label: string;
  hint: string;
  kind: "trip" | "driver" | "page";
  ref?: any;
  link?: string;
}

/** Desk-only actions the single shell can call without remounting the header. */
@Injectable({ providedIn: "root" })
export class RheroShellBridge {
  online = 0;
  alertCount = 0;
  palette: (() => void) | null = null;
  alerts: (() => void) | null = null;
  /** Live fleet from the desk. Each row includes tone, car, and label. */
  drivers: any[] = [];
  driverFilter: "active" | "busy" | "all" = "all";
  searchHits: ShellSearchHit[] = [];
  onSearch: ((query: string) => void) | null = null;
  onPick: ((hit: ShellSearchHit) => void) | null = null;
  onDriver: ((driver: any) => void) | null = null;
  onMessageDriver: ((driver: any) => void) | null = null;
  /** Opens the header-level Messages panel on the desk. */
  onMessages: (() => void) | null = null;
  /** Vehicle finder type name. Empty shows every vehicle. */
  finderType = "";
  onHighlightType: ((typeName: string) => void) | null = null;

  setOnline(count: number): void {
    this.online = count;
  }

  setAlertCount(count: number): void {
    this.alertCount = count;
  }

  publishDrivers(drivers: any[]): void {
    this.drivers = drivers || [];
  }

  /** Last driver query the desk used (city/country scope). The header reuses it so both read the same list. */
  driverParams: any = null;

  /** One status rule for every count: AVAILABLE / AVAILABLE_FOR_SHARE / NEAR_AVAILABLE = available, IN_BOOKING = on trip, BUSINESS_OFF = busy. */
  static toneOf(driver: any): "available" | "ontrip" | "busy" | "offline" {
    switch (Number(driver?.status)) {
      case 6:
      case 8:
      case 9:
        return "available";
      case 7:
        return "ontrip";
      case 10:
        return "busy";
      default:
        return "offline";
    }
  }

  /** Same shape the desk publishes: tone, car (plate or driver no.), label and vehicle type. */
  /**
   * Driver id -> vehicle seen on that driver's most recent trip (plate, make/model, type).
   * The tracking endpoint returns no plate and its vehicle-type lookup comes back empty
   * (server joins "vehicleTypes", the collection is "vehicle_types"), so the desk fills
   * this from trip data, which carries the same vehicle the admin panel shows.
   */
  static vehicleIndex = RheroShellBridge.loadVehicleIndex();

  private static loadVehicleIndex(): Map<string, { plate: string; model: string; type: string; at: number }> {
    try {
      const raw = sessionStorage.getItem("rhero.vehicleIndex");
      return new Map(raw ? JSON.parse(raw) : []);
    } catch {
      return new Map();
    }
  }

  static indexVehicles(trips: any[]): void {
    for (const trip of trips || []) {
      const d = trip?.confirmedDriver;
      const id = String(d?.id || d?._id || "");
      const v = d?.vehicleDetail;
      if (!id || !v?.plateNo) continue;
      const at = Number(trip?.bookingTime) || 0;
      const prev = RheroShellBridge.vehicleIndex.get(id);
      if (prev && prev.at > at) continue;
      const model = [v?.brand?.en || v?.brand, v?.model?.en || v?.model].filter((x: any) => typeof x === "string" && x).join(" ") || String(v?.name || "");
      RheroShellBridge.vehicleIndex.set(id, { plate: String(v.plateNo), model, type: String(trip?.vehicleType?.name || "").trim(), at });
    }
    try {
      sessionStorage.setItem("rhero.vehicleIndex", JSON.stringify([...RheroShellBridge.vehicleIndex.entries()]));
    } catch {
      /* storage unavailable */
    }
  }

  static decorate(driver: any): any {
    const known = RheroShellBridge.vehicleIndex.get(String(driver?._id || driver?.id || ""));
    const plate = driver?.vehicleDetail?.plateNo || driver?.plateNo || driver?.vehicle?.plateNo || known?.plate || driver?.uniqueId;
    const phone = String(driver?.phone || "").replace(/\D/g, "");
    const name = driver?.fullName || `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim() || driver?.name || "Driver";
    return {
      ...driver,
      name,
      tone: RheroShellBridge.toneOf(driver),
      car: plate ? String(plate) : phone.slice(-3) || "—",
      label: name,
      vehicleModel: known?.model || "",
      typeName: String(driver?.typeName || driver?.vehicleType?.name || driver?.vehicleTypeDetail?.name || driver?.vehicleDetail?.vehicleType?.name || known?.type || driver?.vehicleDetail?.name || "").trim()
    };
  }

  /** Online = available drivers in the published list, so the pill always equals the Drivers panel's Available count. */
  syncOnlineFromDrivers(): void {
    this.online = this.drivers.filter((driver) => driver.tone === "available").length;
  }
}
